<template>
  <div class="game-container">
    <div v-if="!gameStarted" class="start-screen">
      <h1>Găsiți obiectele ascunse!</h1>
      <div class="difficulty-select">
        <button
            v-for="level in difficultyLevels"
            :key="level.id"
            @click="startGame(level)"
            :class="['difficulty-btn', level.id]"
        >
          {{ level.name }}
        </button>
      </div>
    </div>

    <div v-else class="game-area">
      <div class="game-controls">
        <div class="stats-row">
          <div class="stats-group">
            <div class="stat-item">⏳ {{ formattedTime }}</div>
            <div class="stat-item">🎯 {{ attempts }}</div>
            <div class="stat-item">⭐ {{ score }}</div>
            <div class="stat-item">🔍 {{ totalDifferences - foundDifferences.length }}</div>
          </div>

          <div class="controls-group">
            <button class="control-btn" @click="endGameAndReturn">
              ⏏️ Înapoi
            </button>
            <button class="control-btn sound-toggle" @click="soundEnabled = !soundEnabled">
              {{ soundEnabled ? '🔊' : '🔇' }}
            </button>
          </div>
        </div>
      </div>

      <div class="image-container" @click="checkClick">
        <img :src="currentImage" alt="game scene" class="game-image" />

        <div
            v-for="(item, index) in differences"
            :key="'diff-' + index"
            class="hidden-item"
            :class="{ found: foundDifferences.includes(index) }"
            :style="getItemStyle(item)"
            v-show="!foundDifferences.includes(index)"
        >
          <img :src="item.icon" class="found-icon" />
        </div>

        <div
            v-for="(mark, index) in clickMarks"
            :key="'mark-' + index"
            class="click-mark"
            :style="mark.style"
        ></div>
      </div>
    </div>

    <div v-if="gameEnded" class="end-screen-overlay">
      <div :class="['end-screen', gameWon ? 'win-screen' : 'lose-screen']">
        <div v-if="gameWon" class="result-content">
          <h2>🎉 Felicitări! 🎉</h2>
          <div class="stats">
            <p>Scor: {{ score }}</p>
            <p>Încercări: {{ attempts }}</p>
            <p>Timp rămas: {{ formattedTime }}</p>
          </div>
          <button @click="startGame(currentDifficulty)">Joacă din nou</button>
        </div>
        <div v-else class="result-content">
          <h2>😞 Timpul a expirat!</h2>
          <div class="stats">
            <p>Ai găsit {{ foundDifferences.length }} din {{ totalDifferences }}</p>
          </div>
          <button @click="startGame(currentDifficulty)">Încearcă din nou</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      soundEnabled: true,
      difficultyLevels: [
        { id: 'easy', name: 'Începător', differences: 3, time: 120, size: 6 },
        { id: 'medium', name: 'Avansat', differences: 5, time: 90, size: 4 },
        { id: 'hard', name: 'Expert', differences: 7, time: 60, size: 3 },
      ],
      images: [
        "https://s3-eu-west-1.amazonaws.com/a10ro/Lot/71415/conversions/erpsync-main-image-details.jpg",
        "https://s3-eu-west-1.amazonaws.com/a10ro/Lot/98423-ro/conversions/erpsync-main-image-details.jpg",
        "https://s3-eu-west-1.amazonaws.com/a10ro/Lot/76485/conversions/erpsync-main-image-details.jpg"
      ],
      icons: [
        '/assets/img/dog.png',
        '/assets/img/detective.png',
        '/assets/img/both.png'
      ],
      gameStarted: false,
      gameEnded: false,
      gameWon: false,
      currentDifficulty: null,
      currentImage: '',
      differences: [],
      foundDifferences: [],
      clickMarks: [],
      attempts: 0,
      timeLeft: 0,
      timer: null,
      audio: {
        click: new Audio('/assets/sounds/click.mp3'),
        success: new Audio('/assets/sounds/success.wav'),
        error: new Audio('/assets/sounds/error.wav'),
        win: new Audio('/assets/sounds/win.wav'),
        lose: new Audio('/assets/sounds/lose.wav')
      }
    }
  },
  computed: {
    totalDifferences() {
      return this.differences.length
    },
    formattedTime() {
      const m = Math.floor(this.timeLeft / 60)
      const s = this.timeLeft % 60
      return `${m}:${s.toString().padStart(2, '0')}`
    },
    score() {
      const timeBonus = this.timeLeft * 10;
      const accuracy = Math.max(0, 1 - (this.attempts / 100));
      return Math.floor(this.foundDifferences.length * 500 + timeBonus + (accuracy * 100));
    }
  },
  methods: {
    endGameAndReturn() {
      if(confirm("Sigur doriți să părăsiți jocul?")) {
        clearInterval(this.timer);
        this.gameStarted = false;
        this.gameEnded = false;
        this.currentImage = '';
        this.foundDifferences = [];
      }
    },
    animateUnfound() {
      const items = document.querySelectorAll('.hidden-item:not(.found)');
      items.forEach(el => {
        // Reset animation to apply changes
        el.style.animation = 'none';
        void el.offsetWidth; // trigger reflow
        el.style.animation = getComputedStyle(el).animation;
      });
    },
    startGame(level) {

      const availableImages = this.images.filter(img => img !== this.currentImage);
      this.currentImage = availableImages.length > 0
          ? availableImages[Math.floor(Math.random() * availableImages.length)]
          : this.images[Math.floor(Math.random() * this.images.length)];
      this.gameStarted = true
      this.gameEnded = false
      this.gameWon = false
      this.currentDifficulty = level
      this.attempts = 0
      this.foundDifferences = []
      this.clickMarks = []
      this.timeLeft = level.time
      this.currentImage = this.images[Math.floor(Math.random() * this.images.length)]

      this.differences = Array.from({ length: level.differences }, () => ({
        x: Math.random() * 90 + 5,
        y: Math.random() * 90 + 5,
        size: level.size,
        icon: this.icons[Math.floor(Math.random() * this.icons.length)]
      }))

      clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.animateUnfound();
        this.timeLeft--
        if (this.timeLeft <= 0) this.endGame(false)
      }, 1000)
    },
    checkClick(e) {
      const rect = e.currentTarget.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const clickY = e.clientY - rect.top

      this.clickMarks.push({
        style: {
          left: `${(clickX / rect.width) * 100}%`,
          top: `${(clickY / rect.height) * 100}%`
        }
      })

      let found = false
      this.differences.forEach((diff, idx) => {
        if (this.foundDifferences.includes(idx)) return
        const diffX = (diff.x / 100) * rect.width
        const diffY = (diff.y / 100) * rect.height
        const distance = Math.sqrt((clickX - diffX) ** 2 + (clickY - diffY) ** 2)
        if (distance < diff.size * 10) {
          this.foundDifferences.push(idx)
          if (this.soundEnabled) this.audio.success.play()
          found = true
          if (this.foundDifferences.length === this.totalDifferences) {
            this.endGame(true)
          }
        }
      })

      if (!found) {
        if (this.soundEnabled) this.audio.error.play()
        this.attempts++
      } else {
        if (this.soundEnabled) this.audio.click.play()
      }
    },
    getItemStyle(item) {
      const isFound = this.foundDifferences.includes(this.differences.indexOf(item));
      const timeElapsed = this.currentDifficulty.time - this.timeLeft;
      const intensity = Math.min(1, timeElapsed / this.currentDifficulty.time);

      return {
        position: 'absolute',
        left: `${item.x}%`,
        top: `${item.y}%`,
        width: '30px',
        height: '30px',
        pointerEvents: 'none',
        opacity: isFound ? 1 : 0.7,
        animation: isFound ? 'none' : `pulse ${1 - intensity * 0.7}s infinite`,
        transform: `scale(${1 + intensity * 0.2})`,
        filter: `brightness(${1 + intensity * 0.5})`
      };
    },
    endGame(won) {
      clearInterval(this.timer);
      this.gameEnded = true;
      this.gameWon = won;

      if (this.soundEnabled) {
        if (won) {
          this.audio.win.play();
        } else {
          this.audio.lose.play();
        }
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.timer)
  }
}
</script>

<style scoped>
.end-screen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
}

.end-screen {
  background: white;
  padding: 40px 60px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  max-width: 90%;
  animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.win-screen {
  background: linear-gradient(145deg, #c8f7c5, #e6ffe6);
  border: 3px solid #4CAF50;
}

.lose-screen {
  background: linear-gradient(145deg, #ffe6e6, #ffd6d6);
  border: 3px solid #F44336;
}

.result-content h2 {
  margin-bottom: 25px;
  font-size: 2rem;
}

.result-content button {
  margin-top: 25px;
  padding: 15px 40px;
  font-size: 1.1rem;
}

.stats {
  margin: 20px 0;
  font-size: 1.1rem;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
.game-controls {
  margin: 20px auto;
  max-width: 1000px;
  padding: 0 15px;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255,255,255,0.9);
  padding: 12px 20px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.stats-group {
  display: flex;
  gap: 25px;
  align-items: center;
}

.stat-item {
  font-size: 1.1rem;
  font-weight: 500;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 8px;
}

.controls-group {
  display: flex;
  gap: 15px;
  align-items: center;
}

.control-btn {
  padding: 8px 15px;
  border: none;
  border-radius: 8px;
  background: #2196F3;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.control-btn:hover {
  background: #1976D2;
  transform: translateY(-2px);
}


/* Keep existing image container styles */
.image-container {
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  border: 2px solid #ccc;
  border-radius: 10px;
  overflow: hidden;
}
.start-screen {
  text-align: center;
  padding: 40px 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: white;
}

.start-screen h1 {
  font-size: 2.5rem;
  margin-bottom: 30px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.difficulty-select {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  margin: 40px 0;
}

.difficulty-btn {
  padding: 18px 35px;
  border: none;
  border-radius: 15px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.difficulty-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.3);
}

.difficulty-btn:active {
  transform: translateY(0);
}

.easy {
  background: linear-gradient(45deg, #4CAF50, #45a049);
  color: white;
}

.medium {
  background: linear-gradient(45deg, #FFC107, #ffab00);
  color: black;
}

.hard {
  background: linear-gradient(45deg, #F44336, #d32f2f);
  color: white;
}

/* Modify play again button */
button {
  padding: 12px 25px;
  border: none;
  border-radius: 8px;
  background: #2196F3;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  margin-top: 20px;
}

button:hover {
  background: #1976D2;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}
.hidden-item {
  transition: all 0.3s ease;
}

@keyframes pulse {
  0% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
  100% { opacity: 0.3; transform: scale(1); }
}

.found {
  opacity: 1 !important;
  animation: pop 0.5s ease-out !important;
}

@keyframes pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}
.image-container {
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  border: 2px solid #ccc;
  border-radius: 10px;
  overflow: hidden;
}

.game-image {
  width: 100%;
  display: block;
}

.hidden-item img {
  width: 100%;
  height: auto;
  animation: pop 0.6s;
}

.click-mark {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid red;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: clickEffect 0.5s forwards, ripple 1s infinite;
}
@keyframes ripple {
  0% { box-shadow: 0 0 0 0 rgba(255,0,0,0.3); }
  100% { box-shadow: 0 0 0 20px rgba(255,0,0,0); }
}
@keyframes pop {
  0% { transform: scale(0.5); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes clickEffect {
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(2); }
}
.win-screen {
  animation: winFlash 1.5s ease-in-out;
}

@keyframes winFlash {
  0% { background-color: #fff; }
  50% { background-color: #c8f7c5; }
  100% { background-color: #fff; }
}

.sound-toggle {
  width: 40px;
  height: 40px;
  padding: 0;
  justify-content: center;
  border-radius: 50%;
}

.sound-toggle:hover {
  background: #555;
}

@keyframes nudge {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(2px, -2px); }
}

.pulse {
  animation: pulseEffect 0.5s ease-in-out;
}

@keyframes pulseEffect {
  0% { transform: scale(1); opacity: 0.3; }
  50% { transform: scale(1.15); opacity: 0.6; }
  100% { transform: scale(1); opacity: 0.3; }
}

</style>
